/**
 * Helper functions for CreateMeetingPopup component
 */

/**
 * Formats time as HH:MM with zero-padding
 */
export const formatTimeString = (hours: number, minutes: number): string => {
  return `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}`
}

/**
 * Rounds minutes to next 15-minute interval
 * Returns both rounded minutes and whether to add an hour
 */
export const roundToNext15Minutes = (
  minutes: number
): { roundedMinutes: number; addHour: boolean } => {
  const roundedMinutes = Math.ceil(minutes / 15) * 15
  return {
    roundedMinutes: roundedMinutes >= 60 ? 0 : roundedMinutes,
    addHour: roundedMinutes >= 60,
  }
}

/**
 * Gets current time in specified timezone with 15-minute rounding
 */
export const getCurrentTimeInTimezone = (timezone: string): { hour: number; minute: number } => {
  const now = new Date()

  try {
    const formatter = new Intl.DateTimeFormat('en-US', {
      timeZone: timezone,
      hour: '2-digit',
      minute: '2-digit',
      hour12: false,
    })

    const parts = formatter.formatToParts(now)
    const hour = parseInt(parts.find((p) => p.type === 'hour')?.value || '0')
    const minute = parseInt(parts.find((p) => p.type === 'minute')?.value || '0')

    const { roundedMinutes, addHour } = roundToNext15Minutes(minute)

    return {
      hour: addHour ? hour + 1 : hour,
      minute: roundedMinutes,
    }
  } catch (error) {
    // Fallback to local time
    const { roundedMinutes, addHour } = roundToNext15Minutes(now.getMinutes())

    return {
      hour: addHour ? now.getHours() + 1 : now.getHours(),
      minute: roundedMinutes,
    }
  }
}

/**
 * Adds minutes to a time string and returns new time
 */
export const addMinutesToTime = (timeString: string, minutesToAdd: number): string => {
  const [hours, minutes] = timeString.split(':').map(Number)
  const endDate = new Date(2000, 0, 1, hours, minutes)
  endDate.setMinutes(endDate.getMinutes() + minutesToAdd)
  return formatTimeString(endDate.getHours(), endDate.getMinutes())
}

/**
 * Formats minute with leading zero
 */
export const formatMinute = (minute: number): string => {
  return minute.toString().padStart(2, '0')
}

/**
 * Gets timezone display string with offset
 */
export const getTimezoneDisplay = (timezone: string): string => {
  try {
    const formatter = new Intl.DateTimeFormat('en-US', {
      timeZone: timezone,
      timeZoneName: 'longOffset',
    })
    const parts = formatter.formatToParts(new Date())
    const offsetPart = parts.find((part) => part.type === 'timeZoneName')
    const formattedOffset = offsetPart ? offsetPart.value : 'GMT+00:00'

    return `(${formattedOffset}) ${timezone.replace('_', ' ')}`
  } catch (error) {
    return '(GMT+00:00) UTC'
  }
}

/**
 * Filters time slots to show only future times for today in local timezone
 */
export const getValidStartTimes = (
  selectedDate: Date | null,
  selectedTimezone: string,
  deviceTimezone: string
): { value: string; display: string }[] => {
  const allSlots = generateTimeSlots()

  if (!selectedDate) return allSlots

  const today = new Date()
  const isToday = selectedDate.toDateString() === today.toDateString()

  if (!isToday) return allSlots
  if (selectedTimezone !== deviceTimezone) return allSlots

  const now = new Date()
  const currentHours = now.getHours()
  const currentMinutes = now.getMinutes()
  const currentTimeInMinutes = currentHours * 60 + currentMinutes

  return allSlots.filter((slot) => {
    const [hours, minutes] = slot.value.split(':').map(Number)
    const slotTimeInMinutes = hours * 60 + minutes
    return slotTimeInMinutes > currentTimeInMinutes
  })
}

/**
 * Filters time slots to show only times after start time
 */
export const getValidEndTimes = (startTime: string): { value: string; display: string }[] => {
  const allSlots = generateTimeSlots()
  if (!startTime) return allSlots

  return allSlots.filter((slot) => slot.value > startTime)
}

/**
 * Formats date for display
 */
export const formatDate = (date: Date): string => {
  return date.toLocaleDateString('en-US', {
    weekday: 'short',
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  })
}

/**
 * Formats time for display (12-hour format)
 */
export const formatTimeDisplay = (timeString: string): string => {
  if (!timeString) return ''
  const [hours, minutes] = timeString.split(':').map(Number)

  // Simple 12-hour format conversion without timezone conversion
  const displayHour = hours === 0 ? 12 : hours > 12 ? hours - 12 : hours
  const displayPeriod = hours < 12 ? 'AM' : 'PM'
  const displayMinute = formatMinute(minutes)

  return `${displayHour}:${displayMinute} ${displayPeriod}`
}

/**
 * Validates email address format
 * - Maximum length of 254 characters (RFC 5321)
 * - Prevents multiple consecutive dots
 * - Validates proper domain structure with TLD
 * - Restricts invalid special characters
 */
export const isValidEmail = (email: string): boolean => {
  if (!email || typeof email !== 'string') {
    return false
  }

  // Check maximum email length (RFC 5321 specifies 254 characters)
  if (email.length > 254) {
    return false
  }

  // Check for multiple consecutive dots anywhere in the email
  if (email.includes('..')) {
    return false
  }

  // Check for invalid special characters (only allow: alphanumeric, dots, hyphens, underscores, plus signs, @)
  const invalidCharRegex = /[^a-zA-Z0-9._+\-@]/
  if (invalidCharRegex.test(email)) {
    return false
  }

  // Split email into local and domain parts
  const parts = email.split('@')
  if (parts.length !== 2) {
    return false
  }

  const [localPart, domainPart] = parts

  // Validate local part (before @)
  // - Cannot be empty
  // - Cannot start or end with a dot
  // - Cannot have consecutive dots (already checked above)
  // - Can contain: alphanumeric, dots, hyphens, underscores, plus signs
  if (
    !localPart ||
    localPart.length === 0 ||
    localPart.startsWith('.') ||
    localPart.endsWith('.') ||
    localPart.startsWith('-') ||
    localPart.endsWith('-')
  ) {
    return false
  }

  // Validate domain part (after @)
  // - Cannot be empty
  // - Must have at least one dot (for TLD)
  // - Cannot start or end with a dot or hyphen
  // - Must have valid TLD (at least 2 characters after last dot)
  if (
    !domainPart ||
    domainPart.length === 0 ||
    !domainPart.includes('.') ||
    domainPart.startsWith('.') ||
    domainPart.endsWith('.') ||
    domainPart.startsWith('-') ||
    domainPart.endsWith('-')
  ) {
    return false
  }

  // Validate TLD (top-level domain)
  const domainParts = domainPart.split('.')
  const tld = domainParts[domainParts.length - 1]
  if (!tld || tld.length < 2 || /[^a-zA-Z0-9]/.test(tld)) {
    return false
  }

  // Validate each domain segment (between dots)
  for (const segment of domainParts) {
    if (!segment || segment.length === 0) {
      return false
    }
    // Domain segments can contain alphanumeric and hyphens, but not start/end with hyphen
    if (segment.startsWith('-') || segment.endsWith('-')) {
      return false
    }
    if (!/^[a-zA-Z0-9-]+$/.test(segment)) {
      return false
    }
  }

  // Final comprehensive regex check for overall structure
  // This regex ensures: proper format, valid characters only, proper domain structure
  // Local part: alphanumeric, can contain dots/hyphens/underscores/plus (not at start/end)
  // Domain part: alphanumeric, can contain dots/hyphens (not at start/end), must have valid TLD
  const emailRegex =
    /^[a-zA-Z0-9]([a-zA-Z0-9._+-]*[a-zA-Z0-9])?@[a-zA-Z0-9]([a-zA-Z0-9-]*[a-zA-Z0-9])?(\.[a-zA-Z0-9]([a-zA-Z0-9-]*[a-zA-Z0-9])?)*\.[a-zA-Z]{2,}$/
  return emailRegex.test(email)
}

/**
 * Generates time slots in 15-minute intervals
 */
export const generateTimeSlots = (): { value: string; display: string }[] => {
  const slots: { value: string; display: string }[] = []

  for (let hour = 0; hour < 24; hour++) {
    for (let minute = 0; minute < 60; minute += 15) {
      const timeString = formatTimeString(hour, minute)

      // Simple 12-hour format display (not timezone converted, just formatted)
      const displayHour = hour === 0 ? 12 : hour > 12 ? hour - 12 : hour
      const displayPeriod = hour < 12 ? 'AM' : 'PM'
      const displayMinute = formatMinute(minute)
      const displayTime = `${displayHour}:${displayMinute} ${displayPeriod}`

      slots.push({ value: timeString, display: displayTime })
    }
  }
  return slots
}

/**
 * Gets user role label based on type and context
 */
export const getUserRoleLabel = (
  userType?: string,
  userId?: string,
  options?: {
    organizerId?: string
    advisorId?: string
    isAdvisor?: boolean
    isExternalGuest?: boolean
  }
): string => {
  // External guests should show nothing
  if (options?.isExternalGuest) {
    return ''
  }

  // Organizer takes priority - show only "Organizer"
  if (options?.organizerId && userId === options.organizerId) {
    return 'Organizer'
  }

  // Check if user is an advisor (BSO Advisor/Admin)
  if (options?.isAdvisor || (options?.advisorId && userId === options.advisorId)) {
    return 'Advisor'
  }

  // All other team members (including Business Owner) show as "Team Member"
  switch (userType) {
    case 'ADMIN':
      return 'Admin'
    case 'OWNER':
    case 'CO_OWNER':
    case 'MEMBER':
      return 'Team Member'
    default:
      return 'Team Member'
  }
}

/**
 * Validates URL format
 * - Requires http/https protocol (case-insensitive)
 * - Domain/subdomain segments must be alphanumeric or hyphen (no consecutive dots)
 * - Must include a valid TLD (2+ letters)
 * - Rejects URLs containing backslashes, control characters, or invalid path characters
 * - Ensures only one URL is present (no concatenated URLs or malformed protocol patterns in path)
 */
export const isValidUrl = (url: string): boolean => {
  if (!url || typeof url !== 'string') {
    return false
  }

  const trimmedUrl = url.trim()

  // Reject URLs containing backslashes (meeting URLs should only use forward slashes)
  if (trimmedUrl.includes('\\')) {
    return false
  }

  // eslint-disable-next-line no-control-regex
  if (/[\x00-\x1F\x7F]/.test(trimmedUrl)) {
    return false
  }

  // Ensure spaces are not present
  if (/\s/.test(trimmedUrl)) {
    return false
  }

  // Check if protocol is missing (case-insensitive check)
  const lowerUrl = trimmedUrl.toLowerCase()
  if (!lowerUrl.startsWith('http://') && !lowerUrl.startsWith('https://')) {
    return false
  }

  // Check for multiple URLs concatenated together (e.g., https://zoom.us/j/123https://zoom.us/j/456)
  // Count occurrences of http:// or https://
  const protocolMatches = trimmedUrl.match(/https?:\/\//gi)
  if (protocolMatches && protocolMatches.length > 1) {
    return false
  }

  const urlWithoutProtocol = trimmedUrl.replace(/^https?:\/\//, '')
  if (/https?:/i.test(urlWithoutProtocol)) {
    return false
  }

  // Check for invalid characters in the path/query portion
  // Reject common problematic characters that shouldn't appear in meeting URLs
  if (/[<>{}|\\^`[\]]/.test(trimmedUrl)) {
    return false
  }

  // Regex breakdown:
  // ^https?:\/\/                          -> required protocol (http or https, case-insensitive with 'i' flag)
  // ([a-zA-Z0-9-]+\.)+                    -> one or more domain segments separated by single dots
  // [a-zA-Z]{2,}                          -> TLD (at least two letters)
  // (:\d+)?                               -> optional port
  // (\/[a-zA-Z0-9._~:/?#@!$&'()*+,;=%-]*)?$ -> optional path/query/hash with safe URL characters only
  const urlRegex =
    /^https?:\/\/([a-zA-Z0-9-]+\.)+[a-zA-Z]{2,}(:\d+)?(\/[a-zA-Z0-9._~:/?#@!$&'()*+,;=%-]*)?$/i

  if (!urlRegex.test(trimmedUrl)) {
    return false
  }

  // Additional validation: Try using URL constructor as a final sanity check
  try {
    const urlObj = new URL(trimmedUrl)
    // Ensure the protocol is http or https
    if (urlObj.protocol !== 'http:' && urlObj.protocol !== 'https:') {
      return false
    }
    // Ensure the hostname is not empty
    if (!urlObj.hostname) {
      return false
    }
    return true
  } catch {
    return false
  }
}
